/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Edit3, ScanLine, Plus, Ticket, Users, CheckCircle2, X, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { billetterieApi, formatApiError } from "@/lib/api";

function fmtEur(v) { return Number(v || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2 }) + " €"; }
function fmtDate(iso) { if (!iso) return "—"; try { return new Date(iso).toLocaleDateString("fr-FR"); } catch { return iso; } }

export default function AdminBilletterieDetail() {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showManualForm, setShowManualForm] = useState(false);
  const [manual, setManual] = useState({ ticket_type_id: "", quantity: 1, buyer_email: "", buyer_name: "Vente manuelle", buyer_phone: "", payment_method: "Espèces" });
  const [issuing, setIssuing] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([billetterieApi.adminGet(id), billetterieApi.adminTickets(id)])
      .then(([e, t]) => { setEvent(e); setTickets(t); })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(load, [id]);

  const issueManual = async (e) => {
    e.preventDefault();
    if (!manual.ticket_type_id) return toast.error("Choisissez un type de billet.");
    setIssuing(true);
    try {
      const res = await billetterieApi.adminCreateManualTickets({ event_id: id, ...manual });
      toast.success(`${res.tickets.length} billet(s) émis.`);
      setShowManualForm(false);
      setManual({ ticket_type_id: "", quantity: 1, buyer_email: "", buyer_name: "Vente manuelle", payment_method: "Espèces" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    setIssuing(false);
  };

  if (loading || !event) return <div className="flex items-center gap-2 text-white/55"><Loader2 className="animate-spin" /> Chargement…</div>;

  const stats = event.stats || { tickets_sold: 0, tickets_used: 0, revenue_ttc: 0 };
  const byType = (event.ticket_types || []).reduce((acc, tt) => {
    const list = tickets.filter((t) => t.ticket_type_id === tt.id);
    acc[tt.id] = { tt, sold: list.length, used: list.filter((t) => t.is_used).length };
    return acc;
  }, {});

  return (
    <div data-testid="admin-bill-detail">
      <header className="mb-6">
        <Link to="/admin/billetterie" className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] text-white/55 hover:text-[#DCB457]"><ArrowLeft size={11} /> Retour</Link>
        <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="eyebrow">Événement</p>
            <h1 className="font-serif-luxe text-3xl md:text-4xl mt-2">{event.name}</h1>
            <p className="text-white/55 text-sm mt-1">{fmtDate(event.date)} · {event.venue_name || event.city}</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Link to={`/admin/billetterie/${id}/scans`} data-testid="admin-bill-scans-link" className="btn-ghost-gold inline-flex items-center gap-2"><Users size={14} /> Liste des arrivées</Link>
            <Link to="/admin/billetterie/scan" className="btn-ghost-gold inline-flex items-center gap-2"><ScanLine size={14} /> Scanner</Link>
            <Link to={`/admin/billetterie/${id}/edit`} className="btn-ghost-gold inline-flex items-center gap-2"><Edit3 size={14} /> Éditer</Link>
            <button onClick={() => setShowManualForm(true)} data-testid="admin-bill-manual-btn" className="btn-gold inline-flex items-center gap-2"><Plus size={14} /> Vente manuelle</button>
          </div>
        </div>
      </header>

      {/* Stats globales */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <StatTile icon={Ticket} label="Billets vendus" value={stats.tickets_sold} />
        <StatTile icon={CheckCircle2} label="Billets utilisés" value={stats.tickets_used} accent={stats.tickets_used > 0} />
        <StatTile icon={Users} label="À venir" value={stats.tickets_sold - stats.tickets_used} />
        <StatTile icon={Ticket} label="Recettes TTC" value={fmtEur(stats.revenue_ttc)} />
      </div>

      {/* Par type */}
      <h2 className="font-serif-luxe text-2xl mb-4">Par catégorie</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {(event.ticket_types || []).map((tt) => {
          const s = byType[tt.id] || { sold: 0, used: 0 };
          return (
            <article key={tt.id} className="card-luxe p-4 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1" style={{ background: tt.color }} />
              <h3 className="font-serif-luxe text-lg">{tt.name}</h3>
              <p className="text-[11px] text-white/55">{fmtEur(tt.price_ttc)}</p>
              <div className="mt-3 flex items-baseline gap-2">
                <span className="text-3xl font-serif-luxe gold-text">{s.sold}</span>
                <span className="text-white/55 text-xs">vendu(s)</span>
              </div>
              {tt.quantity_available && <p className="text-[10px] text-white/45 mt-1">sur {tt.quantity_available}</p>}
              <p className="text-[10px] text-emerald-300/85 mt-2">{s.used} entrée(s) validée(s)</p>
            </article>
          );
        })}
      </div>

      {/* Liste tickets */}
      <h2 className="font-serif-luxe text-2xl mb-4">Liste des billets</h2>
      <div className="card-luxe overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-black/40 text-[10px] uppercase tracking-[0.2em] text-white/55">
            <tr>
              <th className="px-4 py-3 text-left">Code</th>
              <th className="px-4 py-3 text-left">Type</th>
              <th className="px-4 py-3 text-left">Acheteur</th>
              <th className="px-4 py-3 text-left">Statut</th>
              <th className="px-4 py-3 text-right">Prix</th>
              <th className="px-4 py-3 text-left">Émis le</th>
            </tr>
          </thead>
          <tbody>
            {tickets.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-white/45 italic">Aucun billet pour le moment.</td></tr>
            ) : tickets.map((t) => (
              <tr key={t.id} className="border-t border-[#DCB457]/10">
                <td className="px-4 py-3 font-mono text-xs text-[#DCB457]">{t.code}</td>
                <td className="px-4 py-3">{t.ticket_type_name}</td>
                <td className="px-4 py-3 text-white/75">
                  <div>{t.buyer_name}</div>
                  {t.buyer_email && <div className="text-[11px] text-white/45">{t.buyer_email}</div>}
                </td>
                <td className="px-4 py-3">
                  {t.is_used
                    ? <span className="text-[10px] uppercase tracking-[0.16em] border border-emerald-400/40 text-emerald-300 bg-emerald-500/8 px-2 py-0.5">Utilisé</span>
                    : t.is_paid
                      ? <span className="text-[10px] uppercase tracking-[0.16em] border border-[#DCB457]/40 text-[#DCB457] bg-[#DCB457]/8 px-2 py-0.5">Valide</span>
                      : <span className="text-[10px] uppercase tracking-[0.16em] border border-white/15 text-white/55 px-2 py-0.5">En attente</span>}
                </td>
                <td className="px-4 py-3 text-right text-[#DCB457]">{fmtEur(t.unit_price)}</td>
                <td className="px-4 py-3 text-xs text-white/55">{fmtDate(t.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal vente manuelle */}
      {showManualForm && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && setShowManualForm(false)}>
          <div className="card-luxe p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-serif-luxe text-2xl">Vente manuelle</h2>
              <button onClick={() => setShowManualForm(false)} className="text-white/55 hover:text-white"><X size={18} /></button>
            </div>
            <form onSubmit={issueManual} className="space-y-3">
              <select className="input-luxe" value={manual.ticket_type_id} onChange={(e) => setManual({ ...manual, ticket_type_id: e.target.value })} required data-testid="manual-tt">
                <option value="">Type de billet…</option>
                {event.ticket_types.map((tt) => <option key={tt.id} value={tt.id}>{tt.name} — {fmtEur(tt.price_ttc)}</option>)}
              </select>
              <input className="input-luxe" type="number" min="1" max="50" placeholder="Quantité" value={manual.quantity} onChange={(e) => setManual({ ...manual, quantity: Number(e.target.value) })} required />
              <input className="input-luxe" placeholder="Nom acheteur" value={manual.buyer_name} onChange={(e) => setManual({ ...manual, buyer_name: e.target.value })} />
              <input className="input-luxe" type="email" placeholder="Email (optionnel — envoi auto si fourni)" value={manual.buyer_email} onChange={(e) => setManual({ ...manual, buyer_email: e.target.value })} />
              <input className="input-luxe" type="tel" placeholder="Téléphone (optionnel — utile pour le contrôle)" value={manual.buyer_phone} onChange={(e) => setManual({ ...manual, buyer_phone: e.target.value })} />
              <select className="input-luxe" value={manual.payment_method} onChange={(e) => setManual({ ...manual, payment_method: e.target.value })}>
                <option>Espèces</option><option>Chèque</option><option>Carte bancaire</option><option>Virement</option><option>Invitation</option>
              </select>
              <button type="submit" disabled={issuing} data-testid="manual-issue" className="btn-gold w-full inline-flex items-center justify-center gap-2 disabled:opacity-60">
                {issuing ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} Émettre les billets
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function StatTile({ icon: Icon, label, value, accent }) {
  return (
    <div className={`card-luxe p-5 ${accent ? "border-emerald-400/40" : ""}`}>
      <div className="flex items-center justify-between mb-3">
        <p className="eyebrow">{label}</p>
        <Icon size={16} strokeWidth={1.3} className={accent ? "text-emerald-300" : "text-[#DCB457]"} />
      </div>
      <p className="font-serif-luxe text-3xl gold-text">{value}</p>
    </div>
  );
}
